import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountSwitchHeader from '@/components/feature/AccountSwitchHeader';
import PeriodPicker, { monthRange } from '@/components/feature/PeriodPicker';
import type { Period } from '@/components/feature/PeriodPicker';
import { getCouponDetails } from '@/api';
import type { AccountDetails } from '@/api';

export default function CouponDetailsPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(() => monthRange(0));
  const [couponDetails, setCouponDetails] = useState<AccountDetails | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getCouponDetails(1, 50)
      .then((res) => {
        if (mounted) setCouponDetails(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //按账期过滤记录并统计（券记录：发放=收入，使用=支出）
  const periodRecords = couponDetails
    ? couponDetails.records.filter((r) => {
        const day = r.date.replace(/\//g, '-').slice(0, 10);
        return day >= period.start && day <= period.end;
      })
    : [];
  const periodIncome = periodRecords.reduce((s, r) => (r.type === '使用' ? s : s + r.amount), 0);
  const periodExpense = periodRecords.reduce((s, r) => (r.type === '使用' ? s + r.amount : s), 0);

  //加载中/失败
  if (!couponDetails) {
    return (
      <div className="mobile-container pb-10">
        <AccountSwitchHeader current="coupon" />
        <div className="flex flex-col items-center justify-center py-24">
          {error ? (
            <>
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </>
          ) : (
            <>
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  //使用规则：接口返回优先，否则默认
  const couponRules = couponDetails.rules && couponDetails.rules.length > 0
    ? couponDetails.rules
    : [
        '代金券兑换后存入优惠券账户，可在购物时直接抵扣使用',
        '优惠券无使用期限限制，长期有效',
        '每次购物可叠加使用多张优惠券',
        '优惠券不可提现，仅限平台内使用',
      ];

  const hasRecords = periodRecords.length > 0;

  return (
    <div className="mobile-container pb-10">
      {/* Header */}
      <AccountSwitchHeader current="coupon" />

      <div className="px-4 space-y-4 pt-4">
        {/* Balance Card — teal gradient */}
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-cyan-400 to-emerald-500"></div>
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-6 right-10 w-20 h-20 rounded-full bg-white/8"></div>
          <div className="absolute top-6 right-20 w-10 h-10 rounded-full bg-white/12"></div>

          <div className="relative px-5 py-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs font-medium tracking-wide mb-1">优惠券余额</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-xl font-medium">¥</span>
                  <span className="text-white text-4xl font-bold tracking-tight">
                    {Number(couponDetails.balance).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-coupon-2-fill text-white text-xl"></i>
              </div>
            </div>

            {/* Stats row */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期收入</p>
                <p className="text-white font-semibold text-sm">
                  {periodIncome > 0 ? `+¥${periodIncome.toFixed(2)}` : '¥0.00'}
                </p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期支出</p>
                <p className="text-white font-semibold text-sm">¥{periodExpense.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">笔数</p>
                <p className="text-white font-semibold text-sm">{periodRecords.length} 笔</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rules Card */}
        <div className="bg-teal-50 border border-teal-200/70 rounded-2xl px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-information-line text-teal-500 text-base"></i>
            </div>
            <span className="text-teal-700 text-xs font-semibold">优惠券使用说明</span>
          </div>
          <ul className="space-y-2">
            {couponRules.map((rule, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5"></span>
                <span className="text-teal-700/90 text-xs leading-relaxed">{rule}</span>
              </li>
            ))}
          </ul>
          <button
            className="mt-3 flex items-center gap-1.5 text-teal-600 text-xs font-medium cursor-pointer hover:text-teal-700 transition-colors"
            onClick={() => navigate('/voucher-mall')}
          >
            <i className="ri-store-2-line text-sm"></i>
            <span>前往兑换商城</span>
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>

        {/* Period Selector */}
        <PeriodPicker period={period} onChange={setPeriod} />

        {/* Section Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-teal-500"></div>
            <span className="text-sm font-semibold text-foreground-800">优惠券明细</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>
              收入：
              <span className="text-teal-600 font-semibold">¥{periodIncome.toFixed(2)}</span>
            </span>
            <span>
              支出：
              <span className="text-foreground-600 font-medium">¥{periodExpense.toFixed(2)}</span>
            </span>
          </div>
        </div>

        {/* Records or Empty state */}
        {hasRecords ? (
          <div className="space-y-3">
            {periodRecords.map((record) => (
              <div
                key={record.id}
                className="bg-white rounded-2xl px-4 py-4 border border-background-100 hover:border-background-200 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-50">
                      <i className="ri-coupon-2-line text-teal-500 text-sm"></i>
                    </div>
                    <div>
                      <span className="text-foreground-800 text-sm font-semibold">{record.type}</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 text-xs font-medium">
                          {record.amount > 0 ? '收入' : '支出'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className={`text-lg font-bold ${record.amount > 0 ? 'text-teal-600' : 'text-foreground-700'}`}>
                    {record.amount > 0 ? '+' : ''}¥{Math.abs(record.amount).toFixed(2)}
                  </p>
                </div>
                <div className="h-px bg-background-100 mb-3"></div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-400">来源</span>
                    <span className="text-foreground-700 font-medium">{record.source}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-400">说明</span>
                    <span className="text-foreground-600">{record.description}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-400">时间</span>
                    <span className="text-foreground-500">{record.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            {/* Illustration */}
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-full bg-teal-50 flex items-center justify-center">
                <i className="ri-coupon-2-line text-teal-300 text-4xl"></i>
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <i className="ri-search-line text-teal-400 text-sm"></i>
              </div>
            </div>
            <p className="text-foreground-700 text-sm font-semibold mb-1.5">暂无明细记录</p>
            <p className="text-foreground-400 text-xs text-center leading-relaxed">
              本期暂无优惠券收支记录
              <br />
              兑换代金券后即可在此查看明细
            </p>
            <button
              onClick={() => navigate(-1)}
              className="mt-6 px-6 py-2 rounded-full border border-teal-300 text-teal-600 text-xs font-medium hover:bg-teal-50 transition-colors cursor-pointer"
            >
              去兑换代金券
            </button>
          </div>
        )}

        {hasRecords && (
          <div className="flex items-center justify-center gap-2 py-3">
            <div className="h-px w-12 bg-background-200"></div>
            <span className="text-foreground-400 text-xs">已显示全部记录</span>
            <div className="h-px w-12 bg-background-200"></div>
          </div>
        )}
      </div>
    </div>
  );
}